import React from 'react';
import {
  assertSome,
  BannerGroup,
  isNone,
  v2_FallbackProvider as FallbackProvider,
  v2_fetchBannersFallback,
  v2_fetchBranchesFallback,
  v2_fetchProductTaxonsFallback,
  v2_useBanners,
  v2_useCart,
  v2_useProductTaxons,
} from '@crocodille/common';
import { AppPage } from '@framework/page';
import JsonLd from '@framework/json-ld';
import PageMeta from '@framework/page-meta';
import {
  BranchSchemaData,
  buildFastFoodRestaurantSchema,
} from '@modules/schema-org';
import { getAppLayoutWithCart } from '@layouts/app-layout/app-layout';
import { getApp } from '@utils/app';
import { getServerSideTranslationProps } from '@utils/i18n';
import { GetStaticPaths, GetStaticProps } from 'next';
import { ParsedUrlQuery } from 'querystring';
import { useTranslation } from 'react-i18next';
import Carousel from '../../../components/carousel/carousel';
import CarouselSkeleton from '../../../components/carousel/carousel-skeleton';
import MenuPageLayout from '../../../components/menu-page-layout/menu-page-layout';
import ProductTaxonsNavigation from '../../../components/products/product-taxons-navigation/product-taxons-navigation';
import { useMedia } from '../../../hooks/use-media';
import * as routing from '../../../routing';

interface MenuPageProps {
  readonly branch: string;
  readonly fallback: Record<string, unknown>;
  readonly branches?: readonly BranchSchemaData[];
}

const MenuPage: AppPage<MenuPageProps> = ({ branch }) => {
  const { t } = useTranslation();

  const { data: cart } = v2_useCart();
  const { data: taxons } = v2_useProductTaxons(cart?.branchCode ?? branch);

  const isDesktopBannersVersion = useMedia('product-list-columns-3', true);

  const { data: banners } = v2_useBanners(
    isDesktopBannersVersion
      ? BannerGroup.TaxonPageTop
      : BannerGroup.TaxonPageTopMobile,
  );

  return (
    <>
      <PageMeta
        pageTitle={t('menu_page.title')}
        description={t('menu_page.description')}
      />
      <MenuPageLayout>
        <MenuPageLayout.Navigation>
          <ProductTaxonsNavigation>
            <ProductTaxonsNavigation.Item
              label={t('menu_page.title')}
              href={routing.menu()}
              state="emphasized"
            />
            {(taxons ?? []).map((taxon) => (
              <React.Fragment key={taxon.id}>
                {taxon.tags?.includes?.('gift_cards') ? (
                  <ProductTaxonsNavigation.Item
                    label={taxon.name}
                    href={routing.products({
                      taxon: taxon.slug,
                    })}
                  />
                ) : (
                  <ProductTaxonsNavigation.Item
                    label={taxon.name}
                    href={routing.products({ taxon: taxon.slug })}
                  />
                )}
              </React.Fragment>
            ))}
          </ProductTaxonsNavigation>
        </MenuPageLayout.Navigation>
        <MenuPageLayout.Promotions>
          {isNone(banners) || banners?.length === 0 ? (
            <CarouselSkeleton />
          ) : (
            <Carousel label="">
              {banners.map((banner) => (
                <Carousel.Slide
                  key={banner.id}
                  src={banner.image}
                  href={banner.link}
                  label={banner.name}
                />
              ))}
            </Carousel>
          )}
        </MenuPageLayout.Promotions>
        {(taxons ?? []).map((taxon) => (
          <MenuPageLayout.TaxonTile
            key={taxon.id}
            title={taxon.name}
            taxonSlug={taxon.slug}
            imageUrl={taxon.image}
          />
        ))}
      </MenuPageLayout>
    </>
  );
};

MenuPage.getLayout = (
  page, // * Intentionally ignored and not passed to MenuLayout
  pageProps,
) => {
  return (
    <>
      {pageProps.branches?.map((branch) => (
        <JsonLd
          key={branch.name}
          id={`schema-branch-${branch.name}`}
          data={buildFastFoodRestaurantSchema(branch)}
        />
      ))}
      {getAppLayoutWithCart(
        <FallbackProvider fallback={pageProps.fallback ?? {}}>
          {page}
        </FallbackProvider>,
      )}
    </>
  );
};

export const getStaticPaths: GetStaticPaths<
  routing.MenuRouteServerParams & ParsedUrlQuery
> = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<
  MenuPageProps,
  routing.MenuRouteServerParams & ParsedUrlQuery
> = async (context) => {
  const branch = assertSome(context.params?.branch);

  const app = getApp(context);

  const [
    translationProps,
    productsFallback,
    bannersMobileFallback,
    bannersFallback,
    branchesFallback,
  ] = await Promise.all([
    getServerSideTranslationProps(app, ['messages']),
    v2_fetchProductTaxonsFallback(app, branch),
    v2_fetchBannersFallback(app, BannerGroup.TaxonPageTopMobile),
    v2_fetchBannersFallback(app, BannerGroup.TaxonPageTop),
    v2_fetchBranchesFallback(app),
  ]);

  const rawBranches = Object.values(branchesFallback)[0] ?? [];
  const branches: BranchSchemaData[] = rawBranches.map((b) => ({
    name: b.name,
    address: b.address,
    city: b.city,
    openingHours: b.openingHours,
    location: b.location,
    phoneNumber: b.phoneNumber,
    imagePath: b.imagePath,
  }));

  return {
    revalidate: 10 * 60, // 10 minutes
    props: {
      ...translationProps,
      branch,
      branches,
      fallback: {
        ...productsFallback,
        ...bannersMobileFallback,
        ...bannersFallback,
      },
    },
  };
};

export default MenuPage;
